import { useEffect, useId, useState } from "react";
import { Link } from "wouter";
import { useCreateEnrollToken, useEntitlement, useEnvironments } from "../../api/queries";
import { isPaymentRequired } from "../../api/errors";
import { useAuth } from "../../auth/AuthContext";
import { canAdministerFleet, canSeeBilling } from "../../auth/roleView";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { CopyButton } from "../../components/CopyButton";
import { Modal } from "../../components/Modal";
import { CapacityNotice } from "../billing/CapacityNotice";
import { nodeCapacityNotice } from "../billing/entitlementRules";
import "../../components/Form.css";
import "./add-host.css";

interface AddHostModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Mints a join token and shows the operator how to enroll a new host with
 * it. The token is held only in this mutation's transient state — never
 * written to localStorage, a query string, or a log (S-13) — and a fresh one
 * is minted every time the dialog opens (or the chosen environment changes);
 * closing it drops whatever was showing.
 *
 * The environment is a required, explicit choice (G14, #605/ADR-0035 §1):
 * the server no longer defaults an omitted environment to production, so
 * this modal never mints a token before the operator has consciously named
 * one — no host is one click away from joining production by a forgotten
 * default (S-1).
 */
export function AddHostModal({ open, onClose }: AddHostModalProps) {
  const { session } = useAuth();
  const environmentFieldId = useId();
  const [environmentName, setEnvironmentName] = useState("");
  const mintMutation = useCreateEnrollToken();
  const { data: environmentsPage, isPending: environmentsPending } = useEnvironments(
    { limit: 100 },
    { enabled: open },
  );
  const environments = environmentsPage?.items ?? [];
  // Defense-in-depth (A-12, PR #634 security audit condition 1): node:create
  // is admin-only server-side. HostsListPage already gates opening this
  // modal on the same helper, but this modal checks again itself rather
  // than trusting every future caller to remember to — it never fires a
  // mint request for a role that could only ever get a 403.
  const canAdminister = canAdministerFleet(session?.role);
  // admin always holds billing:view too, so this never fires a request the
  // opener could only get a 403 from.
  const canViewBilling = canSeeBilling(session?.role);
  const { data: entitlement } = useEntitlement({ enabled: open && canViewBilling });
  const capacityNotice = entitlement ? nodeCapacityNotice(entitlement) : null;
  // Computed once as a plain boolean (rather than calling the type-guard
  // directly inside the JSX conditions below) so the "else" branch isn't
  // narrowed to `never` — `mintMutation.error` is already `ApiError` once
  // `isError` is true, and TS would otherwise treat "not payment-required"
  // on an already-`ApiError`-typed value as an empty type.
  const paymentRequired = mintMutation.isError && isPaymentRequired(mintMutation.error);

  useEffect(() => {
    if (!open) {
      mintMutation.reset();
      setEnvironmentName("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // No default selection (PR #634 security audit condition 2): environments
  // sort by name, so environments[0] can be a production-class environment
  // in some tenants — auto-selecting it would silently mint a join token
  // against production the instant the list loads, before the operator
  // chose anything. The select's placeholder stays selected until the
  // operator explicitly names one.

  // Mints once the operator has explicitly chosen an environment, and again
  // whenever they pick a different one, since a token is pinned to the
  // environment it was minted for.
  useEffect(() => {
    if (open && environmentName && canAdminister) {
      mintMutation.mutate({ environment: environmentName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, environmentName, canAdminister]);

  const origin = window.location.origin;
  const token = mintMutation.data?.token;
  const expiresAt = mintMutation.data?.expiresAt;
  const oneLiner = token ? `curl -fsSL ${origin}/install.sh | COMEBYE_TOKEN=${token} sh` : "";
  const advanced = token ? `kelpie -server ${origin} -token ${token}` : "";

  if (!canAdminister) {
    return (
      <Modal open={open} title="Add a host" onClose={onClose}>
        <p className="add-host__intro">Ask an admin to enrol a new host — enrolling requires the admin role.</p>
        <div className="form-actions">
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} title="Add a host" onClose={onClose}>
      <p className="add-host__intro">
        Run this on the new host to enroll it. Kelpie only ever connects outbound to the control plane — there's
        nothing to open on the host itself.
      </p>

      <label className="form-field" htmlFor={environmentFieldId}>
        <span>Environment</span>
        <select
          id={environmentFieldId}
          value={environmentName}
          onChange={(e) => setEnvironmentName(e.target.value)}
          required
          disabled={environmentsPending}
        >
          <option value="" disabled>
            {environmentsPending ? "Loading environments…" : "Choose an environment…"}
          </option>
          {environments.map((env) => (
            <option key={env.id} value={env.name}>
              {env.name} ({env.class})
            </option>
          ))}
        </select>
        {!environmentsPending && environments.length === 0 && (
          <span className="form-field__hint">No environments yet — create one first.</span>
        )}
      </label>

      {/* Approaching/at capacity, shown whether or not minting has failed yet
          — an admin about to add a host should see this before they try, not
          only after a refusal. */}
      {!mintMutation.isError && capacityNotice && (
        <CapacityNotice severity={capacityNotice.severity} message={capacityNotice.message} />
      )}

      {mintMutation.isPending && <p className="add-host__status">Minting a join token…</p>}

      {paymentRequired && mintMutation.error && (
        // 402: refused because of a licensed limit, never because anything
        // running was touched (charter §1 safety corollary) — the server's
        // own message already says so; render it verbatim rather than a
        // generic failure so it's legible as a plan problem, not a bug.
        <Banner
          tone="warning"
          title="Machine limit reached"
          className="add-host__payment-banner"
          action={
            <div className="add-host__payment-actions">
              {canViewBilling && (
                <Link href="/billing" className="banner__link">
                  View plan
                </Link>
              )}
              <Button variant="secondary" onClick={() => mintMutation.mutate({ environment: environmentName })}>
                Try again
              </Button>
            </div>
          }
        >
          {mintMutation.error.message}
        </Banner>
      )}

      {mintMutation.isError && !paymentRequired && (
        <div className="add-host__status add-host__status--error" role="alert">
          <p>{mintMutation.error instanceof Error ? mintMutation.error.message : "Couldn't mint a join token."}</p>
          <Button variant="secondary" onClick={() => mintMutation.mutate({ environment: environmentName })}>
            Try again
          </Button>
        </div>
      )}

      {token && expiresAt && (
        <>
          <div className="add-host__command">
            <label className="add-host__label" htmlFor="add-host-oneliner">
              One-liner
            </label>
            <code id="add-host-oneliner" className="add-host__code">
              {oneLiner}
            </code>
            <CopyButton getValue={() => oneLiner} label="Copy" />
          </div>

          <details className="add-host__advanced">
            <summary>Manual / advanced</summary>
            <div className="add-host__command">
              <label className="add-host__label" htmlFor="add-host-advanced">
                Run the agent directly
              </label>
              <code id="add-host-advanced" className="add-host__code">
                {advanced}
              </code>
              <CopyButton getValue={() => advanced} label="Copy" />
            </div>
          </details>

          <p className="add-host__note">
            This token is single-use and expires <strong>{new Date(expiresAt).toLocaleString()}</strong>. Adding
            more than one host? Mint another token for each — a used or expired one won't work twice.
          </p>

          <div className="form-actions">
            <Button variant="secondary" onClick={() => mintMutation.mutate({ environment: environmentName })}>
              Mint another token
            </Button>
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
